import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Wrench, BookUser, Package, BarChart3, Settings, X, LogOut, DollarSign, Database, Terminal, GripVertical, Cpu, BookOpen } from 'lucide-react';
import DispatchIcon from '../icons/DispatchIcon';
import { useAuth } from '../../hooks/useAuth';
import { useRole } from '../../contexts/RoleContext';
import { preferencesApi } from '../../api/users';
import { IS_INTERNAL } from '../../lib/tenantMode';

type NavItem = {
  icon: React.ElementType;
  label: string;
  to: string;          // also used as the stable key for drag-and-drop ordering
  end: boolean;
  /** If set, clicking the item runs this instead of routing. */
  action?: () => void;
};

const openSpellHelper = () => window.dispatchEvent(new Event('spell-helper:open'));

const NAV_DEFAULT: NavItem[] = [
  { icon: LayoutDashboard, label: 'Dashboard',     to: '/',         end: true },
  { icon: Wrench,          label: 'Jobs',           to: '/jobs',     end: false },
  { icon: BookUser,        label: 'Profiles',       to: '/profiles', end: false },
  { icon: Package,         label: 'Parts & Orders', to: '/parts',    end: false },
  { icon: DispatchIcon,    label: 'Dispatch',       to: '/dispatch', end: false },
  { icon: DollarSign,      label: 'Sales',          to: '/sales',    end: false },
  { icon: BarChart3,       label: 'Reports',        to: '/reports',  end: false },
  { icon: BookOpen,        label: 'Spell Helper',   to: '/spell',    end: false, action: openSpellHelper },
];


const NAV_DEV: NavItem[] = [
  { icon: Database, label: 'Legacy Lookup',     to: '/legacy', end: false },
  { icon: Terminal, label: 'Developer Console', to: '/dev',    end: false },
];

const NAV_OFFICE: NavItem[] = [
  { icon: Cpu, label: 'Command Center', to: '/command-center', end: false },
];

const NAV_BOTTOM: NavItem[] = [
  { icon: Settings, label: 'Configuration', to: '/config', end: false },
];

const STORAGE_KEY = 'sidebar_nav_order';

function loadNavOrder(): NavItem[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return NAV_DEFAULT;
    const order: string[] = JSON.parse(saved);
    const sorted = order
      .map(to => NAV_DEFAULT.find(n => n.to === to))
      .filter(Boolean) as NavItem[];
    const missing = NAV_DEFAULT.filter(n => !order.includes(n.to));
    return [...sorted, ...missing];
  } catch {
    return NAV_DEFAULT;
  }
}

// ── Shared NavLink style ──────────────────────────────────────────────────────

function navStyle(isActive: boolean, dim = false): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '8px 12px 8px 28px',
    borderRadius: '8px', textDecoration: 'none',
    fontSize: '13.5px', fontWeight: 500, marginBottom: '1px',
    color: isActive ? '#ffffff' : dim ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.45)',
    background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
    transition: 'background 0.15s, color 0.15s',
    userSelect: 'none',
  };
}

// ── User footer ───────────────────────────────────────────────────────────────

function UserFooter() {
  const { user, logout } = useAuth();
  const { activeRole, activePerson, isOverride } = useRole();
  if (!user) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', marginBottom: '4px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.75)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</p>
        <p style={{ fontSize: '10px', color: isOverride ? '#f97316' : 'rgba(255,255,255,0.3)', margin: 0 }}>
          {activePerson ? `viewing as ${activePerson.name.split(' ')[0]}` : isOverride ? `viewing as ${activeRole}` : user.role}
        </p>
      </div>
      <button
        onClick={logout} title="Sign out"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', flexShrink: 0 }}
        onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
        onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}
      >
        <LogOut size={14} />
      </button>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

interface SidebarProps { isOpen: boolean; toggleSidebar: () => void; }

const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar }) => {
  const { user } = useAuth();
  // Internal-only sections are gated behind both role AND tenancy mode —
  // hosted-tenant builds hide Legacy/Dev/Command Center for everyone, even
  // the developer role.
  const isDev    = user?.role === 'developer' && IS_INTERNAL;
  const isOffice = (user?.role === 'office' || isDev) && IS_INTERNAL;

  const [navItems, setNavItems] = useState<NavItem[]>(loadNavOrder);
  const [dragIdx,  setDragIdx]  = useState<number | null>(null);
  const [overIdx,  setOverIdx]  = useState<number | null>(null);
  const [hovered,  setHovered]  = useState<number | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyOrder = (order: string[]) => {
    const sorted = order
      .map(to => NAV_DEFAULT.find(n => n.to === to))
      .filter(Boolean) as NavItem[];
    const missing = NAV_DEFAULT.filter(n => !order.includes(n.to));
    const merged = [...sorted, ...missing];
    setNavItems(merged);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged.map(n => n.to)));
  };

  // On mount: load personal preference, fall back to company default (developer's order)
  useEffect(() => {
    preferencesApi.get().then(async prefs => {
      if (prefs.nav_order?.length) {
        applyOrder(prefs.nav_order);
      } else {
        // No personal preference — inherit the developer's order as company default
        try {
          const defaults = await preferencesApi.getCompanyDefault();
          if (defaults.nav_order?.length) applyOrder(defaults.nav_order);
        } catch {}
      }
    }).catch(() => {});
  }, []); // runs once on mount

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') toggleSidebar(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [toggleSidebar]);

  const persistOrder = (items: NavItem[]) => {
    const order = items.map(n => n.to);
    // localStorage as instant cache
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
    // debounced API save — avoids hammering if user drags multiple times quickly
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      preferencesApi.save({ nav_order: order }).catch(() => {});
    }, 600);
  };

  const handleDrop = (toIdx: number) => {
    if (dragIdx === null || dragIdx === toIdx) return;
    const next = [...navItems];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(toIdx, 0, moved);
    setNavItems(next);
    persistOrder(next);
    setDragIdx(null);
    setOverIdx(null);
  };

  const hoverEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const el = e.currentTarget;
    if (!el.getAttribute('aria-current')) {
      el.style.background = 'rgba(255,255,255,0.06)';
      el.style.color = 'rgba(255,255,255,0.75)';
    }
  };
  const hoverLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const el = e.currentTarget;
    if (!el.getAttribute('aria-current')) {
      el.style.background = 'transparent';
      el.style.color = 'rgba(255,255,255,0.45)';
    }
  };

  return (
    <>
      {isOpen && (
        <div
          onClick={toggleSidebar}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 40, display: 'none' }}
          className="mobile-overlay"
        />
      )}

      <aside style={{ width: '232px', minWidth: '232px', display: 'flex', flexDirection: 'column', background: '#111111', borderRight: '1px solid rgba(255,255,255,0.06)', height: '100%', flexShrink: 0 }}>

        {/* Logo */}
        <div style={{ height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <img src="/logo.svg" alt="Goes West" style={{ height: '32px', width: 'auto' }} />
          <button
            onClick={toggleSidebar}
            style={{ display: 'none', border: 'none', background: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: '4px', borderRadius: '4px' }}
            className="sidebar-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        {/* Draggable nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {navItems.map((item, i) => (
            <div
              key={item.to}
              draggable
              onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(i); }}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverIdx(i); }}
              onDrop={e => { e.preventDefault(); handleDrop(i); }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                position: 'relative',
                opacity: dragIdx === i ? 0.4 : 1,
                borderRadius: '8px',
                outline: overIdx === i && dragIdx !== i ? '1px solid rgba(99,102,241,0.6)' : 'none',
                background: overIdx === i && dragIdx !== i ? 'rgba(99,102,241,0.08)' : 'transparent',
                transition: 'opacity 0.1s',
              }}
            >
              {/* Grip handle */}
              <GripVertical
                size={12}
                style={{
                  position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                  color: 'rgba(255,255,255,0.2)',
                  opacity: hovered === i ? 1 : 0,
                  transition: 'opacity 0.15s',
                  cursor: 'grab', flexShrink: 0, pointerEvents: 'none',
                }}
              />
              {item.action ? (
                <button
                  onClick={() => { item.action!(); if (isOpen) toggleSidebar(); }}
                  style={{ ...navStyle(false), width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(255,255,255,0.45)';
                  }}
                >
                  <item.icon size={15} style={{ flexShrink: 0 }} />
                  {item.label}
                </button>
              ) : (
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={() => isOpen && toggleSidebar()}
                  style={({ isActive }) => navStyle(isActive)}
                  onMouseEnter={hoverEnter}
                  onMouseLeave={hoverLeave}
                >
                  <item.icon size={15} style={{ flexShrink: 0 }} />
                  {item.label}
                </NavLink>
              )}
            </div>
          ))}

          {isOffice && (
            <>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 4px' }} />
              {NAV_OFFICE.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => isOpen && toggleSidebar()}
                  style={({ isActive }) => navStyle(isActive)}
                  onMouseEnter={e => { const el = e.currentTarget; if (!el.getAttribute('aria-current')) { el.style.background = 'rgba(255,255,255,0.06)'; el.style.color = 'rgba(255,255,255,0.75)'; } }}
                  onMouseLeave={e => { const el = e.currentTarget; if (!el.getAttribute('aria-current')) { el.style.background = 'transparent'; el.style.color = 'rgba(255,255,255,0.45)'; } }}
                >
                  <item.icon size={15} style={{ flexShrink: 0 }} />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}

          {isDev && (
            <>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 4px' }} />
              {NAV_DEV.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => isOpen && toggleSidebar()}
                  style={({ isActive }) => navStyle(isActive, true)}
                  onMouseEnter={e => { const el = e.currentTarget; if (!el.getAttribute('aria-current')) { el.style.background = 'rgba(255,255,255,0.06)'; el.style.color = 'rgba(255,255,255,0.75)'; } }}
                  onMouseLeave={e => { const el = e.currentTarget; if (!el.getAttribute('aria-current')) { el.style.background = 'transparent'; el.style.color = 'rgba(255,255,255,0.35)'; } }}
                >
                  <item.icon size={15} style={{ flexShrink: 0 }} />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* Bottom (Config + user) */}
        <div style={{ padding: '8px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          {NAV_BOTTOM.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => isOpen && toggleSidebar()}
              style={({ isActive }) => ({ ...navStyle(isActive), paddingLeft: '12px' })}
              onMouseEnter={e => { const el = e.currentTarget; if (!el.getAttribute('aria-current')) { el.style.background = 'rgba(255,255,255,0.06)'; el.style.color = 'rgba(255,255,255,0.75)'; } }}
              onMouseLeave={e => { const el = e.currentTarget; if (!el.getAttribute('aria-current')) { el.style.background = 'transparent'; el.style.color = 'rgba(255,255,255,0.45)'; } }}
            >
              <item.icon size={15} style={{ flexShrink: 0 }} />
              {item.label}
            </NavLink>
          ))}
          <UserFooter />
          <div style={{ padding: '6px 4px 2px' }}>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', fontWeight: 500, margin: 0 }}>Goes West</p>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.12)', marginTop: '2px', marginBottom: 0 }}>v0.2.0</p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
